import React from "react";
import {twMerge} from "tailwind-merge";
import SmartIcon from "./SmartIcon.tsx";
import {IconType} from "react-icons";

type AgeRangeFieldProps = {
  className?: string;
  classInput?: string;
  label?: string;
  note?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  icon?: string | IconType;
  disabled?: boolean;
};

const NumberRangeField: React.FC<AgeRangeFieldProps> = (
  {
    className,
    classInput,
    label,
    note,
    value,
    onChange,
    placeholder,
    required,
    icon,
    disabled = false,
  }) => {

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value;

    // If user pressed backspace (input length reduced), clear entire input
    if (input.length < value.length) {
      onChange('');
      return;
    }

    // Remove all characters except digits and dash
    input = input.replace(/[^0-9\-]/g, '');

    // If first digit is 0 and no dash yet, insert dash after it
    if (input.startsWith('0') && !input.includes('-')) {
      input = '0-';
    }

    // If dash doesn't exist after 2 digits (for other cases), insert dash
    if (!input.includes('-') && input.length >= 2) {
      input = input.slice(0, 2) + '-' + input.slice(2);
    }

    // Ensure only 1 dash exists
    const dashIndex = input.indexOf('-');
    input = input.slice(0, dashIndex + 1) + input.slice(dashIndex + 1).replace(/-/g, '');

    // Limit segments to max 3 digits each
    const parts = input.split('-');
    if (parts[0].length > 3) parts[0] = parts[0].slice(0, 3);
    if (parts[1] && parts[1].length > 3) parts[1] = parts[1].slice(0, 3);

    onChange(parts.join('-'));
  };

  const handleBlur = () => {
    const parts = value.split('-').map(part => part.trim()).filter(Boolean);
    if (parts.length === 1) {
      const num = parseInt(parts[0], 10);
      if (num <= 50) {
        onChange(`${num}-100`);
      } else {
        onChange(`0-${num}`);
      }
    } else if (parts.length === 2) {
      let [start, end] = parts.map(n => Math.min(100, Math.max(0, parseInt(n, 10))));
      if (start > end) [start, end] = [end, start]; // Ensure start <= end
      onChange(`${start}-${end}`);
    }
  };

  return (
    <div className={className}>
      <div>
        {label && (
          <div className="flex mb-2 base2 font-semibold">
            {label}
          </div>
        )}
        <div className="relative">
          <input
            name={"age-range-input" + Math.random().toString(36).substring(2, 15)}
            className={twMerge(
              `w-full h-13 px-3.5 bg-n-200 border-2 border-n-200 rounded-xl base2 text-n-700 outline-none transition-colors placeholder:text-n-400/50 focus:bg-transparent dark:bg-n-600 dark:border-n-600 dark:text-n-300 dark:focus:bg-transparent ${
                icon && "pl-[3.125rem]"
              } ${
                value !== "" && "bg-transparent border-n-300/50"
              } ${classInput}`
            )}
            type="text"
            value={value}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
          />
          {icon && (
            <SmartIcon
              className={`absolute top-3.5 left-4 fill-n-400/50 pointer-events-none transition-colors ${
                value !== "" && "fill-n-400"
              }`}
              name={icon}
            />
          )}
        </div>
        {note && <div className="mt-2 base2 text-n-400/50">{note}</div>}
      </div>
    </div>
  );
};

export default NumberRangeField;